import React from "react";
import MirsTable from "components/general/table/Table";
import { useViewport } from 'components/general/useViewPort'
import {
    isLiveStatus,
    isStartListStatus,
    isFinishedStatus
} from "helpers/status";
import CaptionTable from "components/general/table/CaptionTable";
import Status from "components/presentation/Status";
import {
    TableColumnsStartList as ColumnsStartList,
    TableColumnsLive as ColumnsLive,
    TableColumnsLive2 as ColumnsLive2,
    TableColumnsLive3 as ColumnsLive3,
    TableColumnsLiveRepFnl as ColumnsLiveRepFnl,
    TableColumnsFinish as ColumnsFinish,
    TableColumnsFinishRepFnl as ColumnsFinishRepFnl,
    TableColumnsStartListSM as ColumnsStartListSM,
    TableColumnsStartListRepFnlSM as ColumnsStartListRepFnlSM,
    TableColumnsLiveSM as ColumnsLiveSM,
    TableColumnsLiveRepFnlSM as ColumnsLiveRepFnlSM,
    TableColumnsFinishSM as ColumnsFinishSM,
    TableColumnsFinishRepFnlSM as ColumnsFinishRepFnlSM,
} from 'components/sports/bmx/results/TableColumns'
import LoaderSubComponent from "components/general/LoaderSubComponent";

const getConfiguration = (data, resultStatus) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    const scheduleStatus = resultStatus === 'START_LIST' 
    ? 'SCHEDULED' 
    : resultStatus === 'LIVE' || resultStatus === 'INTERMEDIATE' 
        ? 'RUNNING' 
        : resultStatus?.includes('OFFICIAL') 
            ? 'FINISHED' 
            : '';
    
    let QFNL = data?.results?.eventUnitCode.includes('QFNL')
    let SFNL = data?.results?.eventUnitCode.includes('SFNL')
    let FNL = data?.results?.eventUnitCode.includes('-FNL-')

    configuration.caption = <CaptionTable
        leftCol={(QFNL || SFNL) 
            ? <h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription.slice(-6)}</h3>
            : <h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription}</h3>
        }
        rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />}
     />

    if (isStartListStatus(resultStatus)) {
        configuration.columns = ColumnsStartList()
        configuration.sortBy = [{ id: 'StartOrder', desc: false }]
    }

    if (isLiveStatus(resultStatus) && data?.results?.eventUnit?.description.includes('Run 1') && (QFNL || SFNL)) {
        configuration.columns = ColumnsLive()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }else if (isLiveStatus(resultStatus) && data?.results?.eventUnit?.description.includes('Run 2') && (QFNL || SFNL)){
        configuration.columns = ColumnsLive2()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    } else if (isLiveStatus(resultStatus) && data?.results?.eventUnit?.description.includes('Run 3') && (QFNL || SFNL)){
        configuration.columns = ColumnsLive3()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    } else if(isLiveStatus(resultStatus)){
        configuration.columns = ColumnsLiveRepFnl()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    if (isFinishedStatus(resultStatus) && (QFNL || SFNL)) {
        configuration.columns = ColumnsFinish()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    } else if(isFinishedStatus(resultStatus)){
        configuration.columns = ColumnsFinishRepFnl()
        if(FNL)
            configuration.hiddenColumns.push('QualMark')
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    return configuration
}

const getConfigurationSM = (data, resultStatus) => {

    let configuration = {
        caption: null,
        columns: [],
        hiddenColumns: [],
        sortBy: [],
    };

    const scheduleStatus = resultStatus === 'START_LIST' 
    ? 'SCHEDULED' 
    : resultStatus === 'LIVE' || resultStatus === 'INTERMEDIATE' 
        ? 'RUNNING' 
        : resultStatus?.includes('OFFICIAL') 
            ? 'FINISHED' 
            : '';
    
    let QFNL = data?.results?.eventUnitCode.includes('QFNL')
    let SFNL = data?.results?.eventUnitCode.includes('SFNL')
    let FNL = data?.results?.eventUnitCode.includes('-FNL-')

    configuration.caption = <CaptionTable
        leftCol={(QFNL || SFNL) 
            ? <h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription.slice(-6)}</h3>
            : <h3 className="text-dark">{data && data?.results?.eventUnit?.shortDescription}</h3>
        }
        rightCol={<Status typeToPrint="result" scheduleStatus={scheduleStatus} resultStatus={resultStatus} />}
     />

    if (isStartListStatus(resultStatus) && (QFNL || SFNL)) {
        configuration.columns = ColumnsStartListSM()
        configuration.sortBy = [{ id: 'StartOrder', desc: false }]
    }else if (isStartListStatus(resultStatus)){
        configuration.columns = ColumnsStartListRepFnlSM()
        configuration.sortBy = [{ id: 'StartOrder', desc: false }]
    }

    if (isLiveStatus(resultStatus) && (QFNL || SFNL)) {
        configuration.columns = ColumnsLiveSM()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    } else if(isLiveStatus(resultStatus)){
        configuration.columns = ColumnsLiveRepFnlSM()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    if (isFinishedStatus(resultStatus) && (QFNL || SFNL)) {
        configuration.columns = ColumnsFinishSM()
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    } else if(isFinishedStatus(resultStatus)){
        configuration.columns = ColumnsFinishRepFnlSM()
        if(FNL)
            configuration.hiddenColumns.push('QualMark')
        configuration.sortBy = [{ id: 'Rank', desc: false }]
    }

    return configuration
}

const BmxResults = ({ data }) => {
    const { breakPoint } = useViewport();
    let resultStatus = data?.results?.status?.code;
    const rsc = data?.results?.eventUnitCode;

    const config = (breakPoint === "sm" || breakPoint === "xs") ? getConfigurationSM(data, resultStatus) : getConfiguration(data, resultStatus)

    const renderSubComponent = React.useCallback(({ row }) => (
        <LoaderSubComponent component={
            <TableExpanded data={row?.original} config={data?.config} rsc={rsc}/>
        }
        />
    ), [resultStatus]);

    return (
        <>
            {data?.results?.items
                ? 
                <>
                    < MirsTable
                        //Dato to show by row
                        data={data?.results?.items}
                        //Columnas a visualizar 
                        columns={config.columns}
                        caption={config.caption}
                        keyRow='participant-code'
                        sortable={false}
                        hiddenColumns={config.hiddenColumns}
                        initialSortBy={config.sortBy}
                        renderRowSubComponent={renderSubComponent}
                        id={`mirs-table-result-${rsc}`}
                    />
                </>
                : ''
            }
        </>
    );
}

const TableExpanded = React.memo((props) => {
    return
});

export default BmxResults;